import { Row, Col, Card, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import Table from "../../components/list/Table";
import React, { useState, useMemo } from "react";
import Pagination from "../../components/list/Pagination";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { IoClose, IoPlay, IoPause, IoCreateOutline, IoAdd, IoPeopleOutline } from "react-icons/io5";
import {
  useGetCampaignListQuery,
  useDeleteCampaignMutation,
  useActivateCampaignMutation,
  useDeactivateCampaignMutation,
} from "../../features/campaign/campaignApi";
import { Campaign as CampaignType } from "../../features/campaign/campaignTypes";
import CampaignStats from "./components/CampaignStats";
import NewCampaignNameModal from "./components/NewCampaignNameModal";
import NoRecipientModal from "./components/NoRecipientModal";
import DeleteCampaignModal from "./components/DeleteCampaignModal";
import ScheduledThisMonthModal from "./components/ScheduledThisMonthModal";
import FullScreenLoader from "../../components/FullScreenLoader";
import {
  computeCampaignStats,
  formatCampaignType,
  getActiveRecipients,
  getCampaignTypeStyle,
  getScheduledInMonth,
  ScheduledRecipientRow,
} from "./campaignUtils";

const badgeStyle = {
  fontSize: "12px",
  padding: "4px 10px",
  fontWeight: 600,
  borderRadius: "999px",
  display: "inline-flex",
  alignItems: "center",
  whiteSpace: "nowrap" as const,
};

const CampaignTypeColumn = ({ row }: { row: { original: CampaignType } }) => {
  const style = getCampaignTypeStyle(row.original.campaign_type);

  return (
    <span style={{ ...badgeStyle, ...style, border: style.border }}>
      {formatCampaignType(row.original.campaign_type)}
    </span>
  );
};

const StatusColumn = ({ row }: { row: { original: CampaignType } }) => {
  const isActive = row.original.is_active;

  return (
    <span
      style={{
        ...badgeStyle,
        backgroundColor: isActive ? "rgba(25, 135, 84, 0.12)" : "rgba(108, 117, 125, 0.12)",
        color: isActive ? "#198754" : "#6c757d",
        border: `1px solid ${isActive ? "rgba(25, 135, 84, 0.25)" : "rgba(108, 117, 125, 0.25)"}`,
      }}
    >
      {isActive ? "Active" : "Paused"}
    </span>
  );
};

const RecipientsColumn = ({ row }: { row: { original: CampaignType } }) => {
  const navigate = useNavigate();
  const count = getActiveRecipients(row.original).length;

  return (
    <span
      role="button"
      onClick={() => navigate(`/gift/via-campaign/${row.original.campaign_id}/recipients`)}
      style={{
        fontWeight: 500,
        color: count > 0 ? "#19222E" : "#adb5bd",
        cursor: "pointer",
        textDecoration: "underline",
      }}
    >
      {count}
    </span>
  );
};

const PriceColumn = ({ row }: { row: { original: CampaignType } }) => {
  if (row.original.campaign_type === "no_gift") {
    return <span className="text-muted">—</span>;
  }

  const price = row.original.price?.order_price || 0;
  return <span style={{ fontWeight: 500, color: "#495057" }}>${price}</span>;
};

const CreatedByColumn = ({ row }: { row: { original: CampaignType } }) => {
  const user = row.original.user;
  if (!user) return <span className="text-muted">N/A</span>;

  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  return <span style={{ color: "#495057" }}>{name || user.email || "N/A"}</span>;
};

const ACTION_ICON = 16;

const CampaignList = () => {
  const [page, setPage] = useState(1);
  const [noRecipientCampaignId, setNoRecipientCampaignId] = useState<string | null>(null);
  const [campaignToDelete, setCampaignToDelete] = useState<CampaignType | null>(null);
  const [scheduledModal, setScheduledModal] = useState<{
    campaignName?: string;
    rows: ScheduledRecipientRow[];
  } | null>(null);
  const navigate = useNavigate();
  const { data: campaignsResponse, isLoading } = useGetCampaignListQuery({});
  const [deleteCampaign, { isLoading: isDeleting }] = useDeleteCampaignMutation();
  const [activateCampaign] = useActivateCampaignMutation();
  const [deactivateCampaign] = useDeactivateCampaignMutation();

  const campaigns = useMemo(() => {
    const automatedTypes = new Set([
      "automated_birthday",
      "automated_home_anniversary",
    ]);
    const automatedNames = new Set([
      "Automated Birthday Campaign",
      "Automated Home Anniversary Campaign",
    ]);
    return (campaignsResponse?.data || []).filter(
      (campaign) =>
        !automatedTypes.has(campaign.campaign_type) &&
        !automatedNames.has(campaign.campaign_name),
    );
  }, [campaignsResponse]);

  const stats = useMemo(() => computeCampaignStats(campaigns), [campaigns]);
  const currentMonthIndex = new Date().getMonth();
  const currentMonthShort = stats.monthLabel.split(" ")[0];
  const showEmptyState = campaigns.length === 0;

  const openScheduledModal = (
    sourceCampaigns: CampaignType[],
    campaignName?: string,
  ) => {
    setScheduledModal({
      campaignName,
      rows: getScheduledInMonth(sourceCampaigns, currentMonthIndex),
    });
  };

  const ScheduledThisMonthColumn = ({ row }: { row: { original: CampaignType } }) => {
    const campaign = row.original;
    const rows = getScheduledInMonth([campaign], currentMonthIndex);
    const count = rows.length;

    if (!campaign.is_active) {
      return <span className="text-muted">—</span>;
    }

    if (count === 0) {
      return <span style={{ fontWeight: 600, color: "#adb5bd" }}>{count}</span>;
    }

    return (
      <span
        role="button"
        tabIndex={0}
        onClick={() => openScheduledModal([campaign], campaign.campaign_name)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openScheduledModal([campaign], campaign.campaign_name);
          }
        }}
        style={{
          fontWeight: 600,
          color: "#0d6efd",
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {count}
      </span>
    );
  };

  const NameColumn = ({ row }: { row: { original: CampaignType } }) => {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/gift/via-campaign/${row.original.campaign_id}/edit`)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            navigate(`/gift/via-campaign/${row.original.campaign_id}/edit`);
          }
        }}
        style={{
          color: "#19222E",
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {row.original.campaign_name || "Untitled Campaign"}
      </span>
    );
  };

  const handleDeleteClick = (campaign: CampaignType) => {
    setCampaignToDelete(campaign);
  };

  const handleConfirmDelete = async () => {
    if (!campaignToDelete) return;

    try {
      await deleteCampaign(campaignToDelete.campaign_id).unwrap();
      toast.success("Campaign deleted successfully");
      setCampaignToDelete(null);
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || "Failed to delete campaign";
      toast.error(errorMessage);
    }
  };

  const handleStart = async (campaignId: string) => {
    try {
      await activateCampaign(campaignId).unwrap();
      toast.success("Campaign started successfully");
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || "Failed to start campaign";
      toast.error(errorMessage);
    }
  };

  const handleStartClick = (campaign: CampaignType) => {
    if (getActiveRecipients(campaign).length === 0) {
      setNoRecipientCampaignId(campaign.campaign_id);
      return;
    }
    handleStart(campaign.campaign_id);
  };

  const handlePause = async (campaignId: string) => {
    try {
      await deactivateCampaign(campaignId).unwrap();
      toast.success("Campaign paused successfully");
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || "Failed to pause campaign";
      toast.error(errorMessage);
    }
  };

  const wrapActionTip = (id: string, label: string, button: React.ReactElement) => (
    <OverlayTrigger
      placement="top"
      delay={{ show: 250, hide: 80 }}
      popperConfig={{ strategy: "fixed" }}
      overlay={<Tooltip id={id}>{label}</Tooltip>}
    >
      <span className="tracking-action-cell__wrap">{button}</span>
    </OverlayTrigger>
  );

  const ActionColumn = ({ row }: { row: { original: CampaignType } }) => {
    const isActive = row.original.is_active;
    const campaignId = row.original.campaign_id;
    const campaign = row.original;

    return (
      <div className="tracking-action-cell">
        {wrapActionTip(
          `campaign-edit-${campaignId}`,
          "Edit Campaign settings",
          <button
            type="button"
            className="action-icon"
            aria-label="Edit Campaign settings"
            onClick={() => navigate(`/gift/via-campaign/${campaignId}/edit`)}
          >
            <IoCreateOutline size={ACTION_ICON} />
          </button>
        )}
        {wrapActionTip(
          `campaign-recipients-${campaignId}`,
          "Add or edit recipients for this campaign.",
          <button
            type="button"
            className="action-icon"
            aria-label="Add or edit recipients for this campaign."
            onClick={() => navigate(`/gift/via-campaign/${campaignId}/recipients`)}
          >
            <IoPeopleOutline size={ACTION_ICON} />
          </button>
        )}
        {isActive
          ? wrapActionTip(
              `campaign-pause-${campaignId}`,
              "Pause campaign",
              <button
                type="button"
                className="action-icon"
                aria-label="Pause campaign"
                onClick={() => handlePause(campaignId)}
              >
                <IoPause size={ACTION_ICON} />
              </button>
            )
          : wrapActionTip(
              `campaign-start-${campaignId}`,
              "Start campaign",
              <button
                type="button"
                className="action-icon"
                aria-label="Start campaign"
                onClick={() => handleStartClick(campaign)}
              >
                <IoPlay size={ACTION_ICON} />
              </button>
            )}
        {wrapActionTip(
          `campaign-delete-${campaignId}`,
          "Delete campaign",
          <button
            type="button"
            className="action-icon action-icon--danger"
            aria-label="Delete campaign"
            onClick={() => handleDeleteClick(campaign)}
          >
            <IoClose size={ACTION_ICON} />
          </button>
        )}
      </div>
    );
  };

  const columns = [
    {
      id: "name",
      header: "Campaign",
      accessorKey: "campaign_name",
      size: 200,
      cell: NameColumn,
    },
    {
      id: "campaign_type",
      header: "Type",
      accessorKey: "campaign_type",
      size: 120,
      cell: CampaignTypeColumn,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "is_active",
      size: 100,
      cell: StatusColumn,
    },
    {
      id: "recipients",
      header: "Recipients",
      accessorKey: "recipient_list",
      size: 100,
      cell: RecipientsColumn,
    },
    {
      id: "scheduled_this_month",
      header: `Scheduled (${currentMonthShort})`,
      accessorKey: "scheduled_this_month",
      size: 130,
      cell: ScheduledThisMonthColumn,
    },
    {
      id: "price",
      header: "Price",
      accessorKey: "price",
      size: 80,
      cell: PriceColumn,
    },
    {
      id: "created_by",
      header: "Created By",
      accessorKey: "created_by",
      size: 150,
      cell: CreatedByColumn,
    },
    {
      id: "action",
      header: "Actions",
      accessorKey: "action",
      size: 110,
      cell: ActionColumn,
    },
  ];

  const [showNameModal, setShowNameModal] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState("Untitled");

  const handleStartNewCampaign = () => {
    setNewCampaignName("Untitled");
    setShowNameModal(true);
  };

  const handleConfirmNewCampaign = () => {
    const name = newCampaignName.trim() || "Untitled";
    setShowNameModal(false);
    navigate("/gift/via-campaign/new", { state: { campaignName: name } });
  };

  // Same pattern as Dashboard / Automated Gifts: one full-screen loader only.
  if (isLoading) {
    return <FullScreenLoader />;
  }

  return (
    <Row>
      <Col>
        <Card
          style={{
            border: "none",
            borderLeft: "1px solid rgba(25, 34, 46, 0.08)",
            borderRadius: 0,
            minHeight: "100vh",
            backgroundColor: "transparent",
          }}
        >
          <Card.Body style={{ padding: 0, backgroundColor: "transparent" }}>
            <div
              className="sticky-filter-bar"
              style={{
                position: "sticky",
                top: "50px",
                zIndex: 100,
                marginBottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "16px",
                padding: "16px 24px",
                borderBottom: "1px solid #eef0f2",
                backgroundColor: "#fff",
              }}
            >
              <div>
                <h5 className="mb-1 fw-semibold" style={{ color: "#19222E" }}>
                  Campaigns
                </h5>
                <p className="mb-0 text-muted" style={{ fontSize: "13px" }}>
                  Automate gift sends for birthdays, anniversaries, and more
                </p>
              </div>
              <Button
                variant="primary"
                onClick={handleStartNewCampaign}
                className="d-inline-flex align-items-center gap-2"
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: 600 }}
              >
                <IoAdd size={18} />
                Create Campaign
              </Button>
            </div>

            <div style={{ padding: "20px 24px 24px" }}>
              <CampaignStats
                totalCampaigns={stats.totalCampaigns}
                activeCampaigns={stats.activeCampaigns}
                scheduledThisMonth={stats.scheduledThisMonth}
                totalRecipients={stats.totalRecipients}
                monthLabel={stats.monthLabel}
                onScheduledClick={
                  stats.scheduledThisMonth > 0
                    ? () => openScheduledModal(campaigns)
                    : undefined
                }
              />

              <div
                style={{
                  background: "#fff",
                  border: "1px solid #eef0f2",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                {showEmptyState ? (
                  <div className="text-center" style={{ padding: "64px 24px" }}>
                    <div
                      className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: "16px",
                        backgroundColor: "rgba(13, 110, 253, 0.08)",
                        color: "#0d6efd",
                      }}
                    >
                      <IoAdd size={28} />
                    </div>
                    <h5 className="fw-semibold mb-2" style={{ color: "#19222E" }}>
                      No campaigns yet
                    </h5>
                    <p
                      className="text-muted mb-4"
                      style={{ maxWidth: 420, margin: "0 auto 24px" }}
                    >
                      Create your first campaign to schedule automated gifts and messages for your recipients.
                    </p>
                    <Button variant="primary" onClick={handleStartNewCampaign}>
                      Create Campaign
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="table-wrapper campaign-table-wrapper">
                      <Table
                        columns={columns}
                        data={campaigns}
                        isSearchable={true}
                        isSortable={true}
                        pagination={true}
                        theadClass="table-light"
                        tableClass="campaign-table"
                        pageSize={20}
                      />
                    </div>
                    {campaigns.length > 0 && (
                      <div style={{ padding: "16px 20px", borderTop: "1px solid #eef0f2" }}>
                        <Pagination
                          currentPage={page}
                          totalItems={campaigns.length}
                          itemsPerPage={20}
                          onPageChange={(nextPage: number) => {
                            setPage(nextPage);
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>

      <NewCampaignNameModal
        show={showNameModal}
        name={newCampaignName}
        onNameChange={setNewCampaignName}
        onHide={() => setShowNameModal(false)}
        onConfirm={handleConfirmNewCampaign}
      />

      <NoRecipientModal
        show={!!noRecipientCampaignId}
        onHide={() => setNoRecipientCampaignId(null)}
        onAddRecipient={() => {
          if (noRecipientCampaignId) {
            navigate(`/gift/via-campaign/${noRecipientCampaignId}/recipients`);
          }
          setNoRecipientCampaignId(null);
        }}
        onStartAnyway={() => {
          if (noRecipientCampaignId) {
            handleStart(noRecipientCampaignId);
          }
          setNoRecipientCampaignId(null);
        }}
      />

      <DeleteCampaignModal
        show={!!campaignToDelete}
        campaignName={campaignToDelete?.campaign_name}
        isDeleting={isDeleting}
        onHide={() => {
          if (!isDeleting) setCampaignToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />

      <ScheduledThisMonthModal
        show={!!scheduledModal}
        monthLabel={stats.monthLabel}
        campaignName={scheduledModal?.campaignName}
        rows={scheduledModal?.rows || []}
        onHide={() => setScheduledModal(null)}
      />
    </Row>
  );
};

export default CampaignList;
